import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LegacyDashboardFilters } from '../../models/Filters';
import { countActiveLegacyDashboardFilters } from '../../utils/applyLegacyDashboardFilters';
import { LegacyDashboardFiltersPanel } from './LegacyDashboardFiltersPanel';
import { DownloadExcelButton } from './DownloadExcelButton';
import type { LegacyDashboardClient } from '../../models/LegacyDashboardClient';

interface FiltersSectionProps {
  searchTerm: string;
  onSearch: (value: string) => void;
  filters: LegacyDashboardFilters;
  onFiltersChange: (filters: LegacyDashboardFilters) => void;
  clients: LegacyDashboardClient[];
  paymentProfileOptions: { value: string; label: string }[];
  shopChannelOptions: { value: string; label: string }[];
  countryOptions: { value: string; label: string }[];
  migrationStatusOptions: { value: string; label: string }[];
}

export const FiltersSection = ({
  searchTerm,
  onSearch,
  filters,
  onFiltersChange,
  clients,
  paymentProfileOptions,
  shopChannelOptions,
  countryOptions,
  migrationStatusOptions,
}: FiltersSectionProps) => {
  const { t } = useTranslation('legacyDashboardModule');
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const activeFiltersCount = useMemo(() => countActiveLegacyDashboardFilters(filters), [filters]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    onSearch(inputValue.trim());
  };

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="legacy-dashboard-search"
        inputDataTestId="legacy-dashboard-search-input"
      />

      <FiltersDropdown
        title={t('filters.title')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="legacy-dashboard-filters-toggle"
      >
        <LegacyDashboardFiltersPanel
          filters={filters}
          onChange={onFiltersChange}
          paymentProfileOptions={paymentProfileOptions}
          shopChannelOptions={shopChannelOptions}
          countryOptions={countryOptions}
          migrationStatusOptions={migrationStatusOptions}
        />
      </FiltersDropdown>

      <DownloadExcelButton clients={clients} />
    </>
  );
};
